import paypalSmartButtons from './../paypal-smart-buttons/index';
import { getSessionToken } from './../paypal-smart-buttons/paypal-utils';
const { DELIVERY_TYPE_FILTER } = require('./../paypal-smart-buttons/paypal-constants');
import CreateOrder from './../paypal-smart-buttons/create-order';
import ApproveOrder from './../paypal-smart-buttons/approve-order';
import CancelOrder from './../paypal-smart-buttons/cancel';
const UpdateOrder = require('./update');
import getShippingMethods from './shipping';

let GooglePayConfig = null;
let shippingMethods = {};
let taxAmount = 0;
let deliveryFee = 0;

const baseRequest = {
	apiVersion: 2,
	apiVersionMinor: 0,
};

/**
 * Check if paypal Google pay sdk is loaded
 * @public
 * @returns {boolean} Returns TRUE if paypal sdk is loaded
 */
const googlePayReady = () => {
	try {
		return google && google.payments; // eslint-disable-line
	} catch (error) {
		return false;
	}
};

/**
 * Initialize Paypal Google Pay Button
 * @public
 * @param {Object | string} elemOrSelector Either dom element(s), or a css selector, targeted for smart button insertion
 * @param {Object} opts Style options used to style and format the paypal smart buttons
 * @param {function} callback Callback function to call once the button has been rendered
 * @param {number} retry The number of times to attempt to reload the buttons. Useful if we need to wait for the SDK to completly load
 * @returns {void}
 */
const initGooglePayButton = (elemOrSelector, opts, callback, retry = 10) => {
	paypalSmartButtons.waitForPaypalScript(retry, () => {
		waitForGooglePayScript(retry, () => {
			renderButton(elemOrSelector, opts);
		});
	});
};

const waitForGooglePayScript = (attempts, callback, error) => {
	const googlePayIsReady = googlePayReady();

	if (!googlePayIsReady && attempts > 0) {
		setTimeout(() => {
			waitForGooglePayScript(attempts - 1, callback, error);
		}, 150);
		return;
	} else if (!googlePayIsReady && attempts === 0) {
		return;
	}

	callback();
};

/**
 * Initialize an Google Pay Button
 * @public
 * @param {string} selector Id selector, targeted for button insertion
 * @returns {void}
 */
const renderButton = (selector, opts) => {
	getConfig()
		.then((googlePayConfig) => {
			GooglePayConfig = googlePayConfig;
			const paymentsClient = getPaymentsClient(opts.loadingCallback, opts.environment);
			paymentsClient.isReadyToPay(getIsReadyToPayRequest(googlePayConfig.allowedPaymentMethods))
				.then((response) => {
					if (response.result) {
						showButton(selector, paymentsClient, googlePayConfig);
					}
				});
		});
};

/**
 *  Return an active PaymentsClient
 * @see {@link https://developers.google.com/pay/api/web/reference/client#PaymentsClient|PaymentsClient constructor}
 * @returns {google.payments.api.PaymentsClient} Google Pay API client
 */
const getPaymentsClient = (loadingCallback, environment) => {
	const paymentsClient = new google.payments.api.PaymentsClient({ // eslint-disable-line no-undef
		environment: environment,
		paymentDataCallbacks: {
			onPaymentAuthorized: (paymentData) => onPaymentAuthorized(paymentData, loadingCallback),
			onPaymentDataChanged: (intermediatePaymentData) => onPaymentDataChanged(intermediatePaymentData),
		}
	});

	return paymentsClient;
};

/**
 * Return Google pay config from paypal_sdk
 * @return {Promise} Google pay config
 */
const getConfig = () => {
	const googlePayConfig = window.paypal_sdk.Googlepay().config(); // eslint-disable-line no-undef
	return googlePayConfig;
};

function getIsReadyToPayRequest(allowedPaymentMethods) {
	return Object.assign({}, baseRequest, {
		allowedPaymentMethods: allowedPaymentMethods,
	});
}

/**
 * Append Google Pay Button to DOM
 * @public
 * @param {string} selector Id selector, targeted for button insertion
 * @returns {void}
 */
const showButton = (selector, paymentsClient, googlePayConfig) => {
	const button = paymentsClient.createButton({
		buttonColor: 'white',
		buttonType: 'plain',
		buttonSizeMode: 'fill',
		onClick: () => handleGooglePayButtonClick(googlePayConfig, paymentsClient),
	});
	document.querySelector(selector).appendChild(button);
};

const handleGooglePayButtonClick = (googlePayConfig, paymentsClient) => {
	const transactionInfo = getTransactionInfo(googlePayConfig);
	const paymentDataRequest = getPaymentDataRequest(googlePayConfig, transactionInfo);

	paymentsClient.loadPaymentData(paymentDataRequest);
	CreateOrder({trigger_begin_checkout_event: true})
		.then((orderId) => {
			localStorage.setItem('rp_googlepay_order_id', orderId);
		});
};

const getTransactionInfo = (googlePayConfig) => {
	const total = window.rp_app.cart.subtotal;
	return {
		displayItems: [
			{
				label: 'Subtotal',
				price: total.toString(),
				type: 'SUBTOTAL',
			}
		],
		countryCode: googlePayConfig.countryCode,
		currencyCode: window.rp_app.cart._currency_code,
		totalPriceStatus: 'FINAL',
		totalPrice: total.toString(),
		totalPriceLabel: 'Total',
	};
};

const getPaymentDataRequest = (googlePayConfig, transactionInfo) => {
	const paymentDataRequest = Object.assign({}, baseRequest);

	paymentDataRequest.allowedPaymentMethods = googlePayConfig.allowedPaymentMethods;
	paymentDataRequest.transactionInfo = transactionInfo;

	paymentDataRequest.merchantInfo = googlePayConfig.merchantInfo;
	paymentDataRequest.callbackIntents = ['PAYMENT_AUTHORIZATION', 'SHIPPING_ADDRESS', 'SHIPPING_OPTION'];

	paymentDataRequest.emailRequired = true;
	paymentDataRequest.shippingAddressRequired = true;
	paymentDataRequest.shippingAddressParameters = {
		allowedCountryCodes: ['US', 'CA'],
		phoneNumberRequired: true,
	};
	paymentDataRequest.shippingOptionRequired = true;

	return paymentDataRequest;
};

const onPaymentAuthorized = (paymentData, loadingCallback) => {
	return new Promise((resolve) => {
		processPayment(paymentData, loadingCallback)
			.then(() => {
				resolve({ transactionState: 'SUCCESS' });
			})
			.catch(() => {
				resolve({ transactionState: 'ERROR' });
			});
	});
};

const onPaymentDataChanged = (intermediatePaymentData) => {
	return new Promise((resolve) => {
		const countryCode = intermediatePaymentData.shippingAddress.countryCode;
		const postalCode = intermediatePaymentData.shippingAddress.postalCode;
		const state = intermediatePaymentData.shippingAddress.administrativeArea;
		const shippingOption = intermediatePaymentData.shippingOptionData;

		let paymentDataRequestUpdate = {};

		if (intermediatePaymentData.callbackTrigger === 'INITIALIZE' || intermediatePaymentData.callbackTrigger === 'SHIPPING_ADDRESS') {
			if (intermediatePaymentData.shippingAddress.administrativeArea === 'NJ') {
				paymentDataRequestUpdate.error = {
					reason: 'SHIPPING_ADDRESS_UNSERVICEABLE',
					message: 'Cannot ship to the selected address',
					intent: 'SHIPPING_ADDRESS'
				};
				resolve(paymentDataRequestUpdate);
			} else {
				getShippingMethods(countryCode, postalCode)
					.then((data) => {
						paymentDataRequestUpdate.newShippingOptionParameters = {
							defaultSelectedOptionId: `${data.shippingMethods.selectedId}`,
							shippingOptions: getShippingOptions(sortShippingMethods(data.shippingMethods.methods)),
						};

						setShippingMethodsToDictionary(data.shippingMethods.methods);

						paymentDataRequestUpdate.newTransactionInfo = calculateNewTransactionInfo(data.order, state);

						resolve(paymentDataRequestUpdate);
					})
					.catch(() => {
						paymentDataRequestUpdate.error = {
							reason: 'SHIPPING_ADDRESS_UNSERVICEABLE',
							message: 'Something went wrong while getting shipping methods',
							intent: 'SHIPPING_ADDRESS'
						};
						resolve(paymentDataRequestUpdate);
					});
			}
		} else if (intermediatePaymentData.callbackTrigger === 'SHIPPING_OPTION') {
			const shippingPrice = getShippingMethodsFromDictionary(shippingOption.id);
			getShippingMethods(null, null, {id: shippingOption.id, value: shippingPrice})
				.then((data) => {
					paymentDataRequestUpdate.newTransactionInfo = calculateNewTransactionInfo(data.order, state);

					resolve(paymentDataRequestUpdate);
				})
				.catch(() => {
					paymentDataRequestUpdate.error = {
						reason: 'SHIPPING_ADDRESS_UNSERVICEABLE',
						message: 'Something went wrong while update the shipping method',
						intent: 'SHIPPING_ADDRESS'
					};
					resolve(paymentDataRequestUpdate);
				});
		}
	});
};

const calculateNewTransactionInfo = (order, state) => {
	let newTransactionInfo = getTransactionInfo(GooglePayConfig);

	newTransactionInfo.displayItems.push({
		type: 'LINE_ITEM',
		label: 'Shipping',
		price: `${order.shippingAmount}`,
		status: 'FINAL',
	});

	newTransactionInfo.displayItems.push({
		type: 'LINE_ITEM',
		label: 'Tax',
		price: `${order.taxAmount}`,
		status: 'FINAL',
	});

	if (order.deliveryFeeAmount) {
		newTransactionInfo.displayItems.push({
			type: 'LINE_ITEM',
			label: state === 'MN' ? 'Road Improvement and Food Delivery Fee' : 'Retail Delivery Fee',
			price: `${order.deliveryFeeAmount}`,
			status: 'FINAL',
		});
		deliveryFee = order.deliveryFeeAmount;
	}

	taxAmount = order.taxAmount;
	newTransactionInfo.totalPrice = `${order.totalAmount}`;

	return newTransactionInfo;
};

const getShippingOptions = (shippingMethods = []) => {
	return shippingMethods.map((shippingMethod) => ({
		id: `${shippingMethod.id}`,
		label: `${shippingMethod.deliveryTypeLabel} ${shippingMethod.amount ? '$': ''}${shippingMethod.amount || 'Free'}`,
		description: shippingMethod.deliveryWindowLabel ?`Est. ${shippingMethod.deliveryWindowLabel}` : '',
	}));
};

const setShippingMethodsToDictionary = (shippingMethod) => {
	shippingMethod.forEach((method) => {
		shippingMethods[method.id] = method.amount;
	});
};

const getShippingMethodsFromDictionary = (shippingMethodId) => {
	return shippingMethods[shippingMethodId] || null;
};

const actions = {
	redirect: (url) => {
		window.location.href = url;
	}
};

function sortShippingMethods(methods) {
	return methods
		// Sort options by ascending amount
		.sort((a, b) => parseFloat(a.amount) - parseFloat(b.amount))
		// Move pre-defined non shipping items to end of array (Pickup)
		.filter(method => !DELIVERY_TYPE_FILTER.includes(method.deliveryType))
		.concat(methods.filter(method => DELIVERY_TYPE_FILTER.includes(method.deliveryType)));
}

const processPayment = (paymentData, loadingCallback) => {
	const cancelOrder = CancelOrder(loadingCallback);
	const approveOrder = ApproveOrder(loadingCallback);

	const googlePaySdk = window.paypal_sdk.Googlepay();
	const ship = paymentData.shippingAddress;
	const bill = paymentData.paymentMethodData.info.billingAddress;
	const safeAddressLine = (addr) =>
		addr.address1 || [addr.locality, addr.administrativeArea, addr.postalCode].filter(Boolean).join(', ') || '';

	const shippingInformation = {
		shippingAddress: {
			name: ship.name,
			address: safeAddressLine(ship),
			city: ship.locality,
			state: ship.administrativeArea,
			postalCode: ship.postalCode,
			country: ship.countryCode,
			phone: ship.phoneNumber,
		},
		billingAddress: {
			name: bill.name,
			address: safeAddressLine(bill),
			city: bill.locality,
			state: bill.administrativeArea,
			postalCode: bill.postalCode,
			country: bill.countryCode,
			phone: '',
		}
	};
	const customer = {
		fullName: paymentData.paymentMethodData.info.billingAddress.name,
		email: paymentData.email,
		phone: paymentData.shippingAddress.phoneNumber,
	};
	cancelOrder();
	return new Promise((resolve) => {
		CreateOrder({
			taxAmount: taxAmount,
			deliveryFee: deliveryFee,
			trigger_begin_checkout_event: false
		})
			.then((orderId) => {
				const shippingId = paymentData.shippingOptionData.id;

				googlePaySdk.confirmOrder({
					orderId: orderId,
					paymentMethodData: paymentData.paymentMethodData,
				})
					.then((response) => {
						if (response.status === 'APPROVED') {
							const sessionToken = getSessionToken();

							UpdateOrder({
								shippingAddress: shippingInformation.shippingAddress,
								billingAddress: shippingInformation.billingAddress,
								sessionToken: sessionToken,
								shippingId: shippingId,
								customer: customer,
							})
								.then((data) => {
									if (data.redirect) {
										cancelOrder();
										resolve({
											transactionState: 'ERROR',
											error: {
												message: 'Something went wrong'
											},
										});
										actions.redirect(data.redirect);
									}

									approveOrder({
										orderID: orderId,
										gateway: 'PAYPALCP_GP'
									}, actions);

									resolve({
										transactionState: 'SUCCESS'
									});
								})
								.catch((err) => {
									cancelOrder();
									resolve({
										transactionState: 'ERROR',
										error: {
											message: err.message,
										},
									});
								});
						}
					})
					.catch((err) => {
						cancelOrder();
						resolve({
							transactionState: 'ERROR',
							error: {
								message: err.message,
							},
						});
					});
			})
			.catch((err) => {
				cancelOrder();
				resolve({
					transactionState: 'ERROR',
					error: {
						message: err.message,
					},
				});
			});
	});
};

export default {
	initGooglePayButton,
};
